// =============================================================================
// WholesaleCostCard — Wholesale billing breakdown for an order (Portal)
// =============================================================================
//
// Displays what Transfer Kingdom will invoice the merchant for this order:
// production cost + shipping cost + total. All values come from the snapshot
// written at order ingest (see `OrderCostService`), so later admin edits of
// variant cost or flat-rate shipping do NOT retro-actively mutate historical
// invoices — the ledger is immutable.
//
// The card is intentionally read-only. Merchants manage their wallet +
// payment methods on the dedicated Wallet / Payment methods pages. We
// never collect payment details inside this card.
// =============================================================================

import { Button, Card } from '@tkpod/ui';
import { WholesaleChargeStatus } from '@tkpod/shared';
import { Link } from 'react-router';

import { SettlementBadge } from './SettlementBadge';

// Merchant-friendly explanation of WHY an order is held/failed, mapped from the
// internal hold reason / Stripe failure code. Mirrors the admin failed-payments
// view so both surfaces explain the same situation identically.
const HOLD_REASON_GUIDANCE: Record<string, string> = {
  NO_PAYMENT_METHOD:
    'No default card is on file. Add a card on your payment methods page, then retry.',
  CARD_DECLINED:
    'Your default card was declined. Update it or top up your wallet, then retry.',
  SCA_REQUIRED:
    'Your bank requires authentication for this card. Add funds to your wallet to settle, or use a different default card.',
  MANUAL_REVIEW:
    'This charge is held for manual review. Our team will follow up shortly.',
  WALLET_FROZEN:
    'Your wallet is temporarily frozen. Contact support to resolve, then retry.',
  CARD_PENDING: 'The card payment is still processing. Check back shortly.',
  INSUFFICIENT_BALANCE:
    'Your wallet balance is too low to cover this order. Add funds or set a default card, then retry.',
  DUNNING_EXHAUSTED:
    'Automatic retries were exhausted. Add funds or fix your default card, then retry.',
};

interface WholesaleCostCardProps {
  productionCost: string | null;
  shippingCost: string | null;
  totalCost: string | null;
  currency: string;
  computedAt: string | null;
  customerPaidTotal: string;
  // Wholesale settlement lifecycle — drives the badge + the merchant Retry
  // affordance. Optional so legacy callers (no settlement data) still render.
  chargeStatus?: string | null;
  holdReason?: string | null;
  onRetry?: () => void;
  retrying?: boolean;
}

function formatMoney(value: string | null, currency: string): string {
  if (value == null) return '—';
  const num = Number.parseFloat(value);
  if (!Number.isFinite(num)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
}

export function WholesaleCostCard({
  productionCost,
  shippingCost,
  totalCost,
  currency,
  computedAt,
  customerPaidTotal,
  chargeStatus,
  holdReason,
  onRetry,
  retrying,
}: WholesaleCostCardProps) {
  const isReady = totalCost !== null;

  // A settlement is "actionable" (needs the merchant to top up / fix a card
  // and retry) only for the non-terminal, non-NONE states. PAID + NONE never
  // show guidance or a Retry button.
  const status = chargeStatus ?? null;
  const needsAttention =
    status === WholesaleChargeStatus.PENDING ||
    status === WholesaleChargeStatus.ON_HOLD ||
    status === WholesaleChargeStatus.FAILED;
  const guidance =
    needsAttention && holdReason ? HOLD_REASON_GUIDANCE[holdReason] : null;

  return (
    <Card>
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-3 sm:px-5">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-text-primary">
            Wholesale cost (billed to you by TK)
          </h2>
          <p className="mt-0.5 text-[11px] text-text-secondary">
            What Transfer Kingdom debits from your prepaid wallet when
            the order is received. Production starts once the charge
            clears.
          </p>
        </div>
        {status && status !== WholesaleChargeStatus.NONE && (
          <SettlementBadge status={status} />
        )}
      </div>

      <div className="border-t border-border px-4 py-3 sm:px-5">
        {!isReady ? (
          <p className="text-xs text-text-tertiary">
            The wholesale cost for this order has not been computed yet. It
            becomes available a few seconds after order ingest.
          </p>
        ) : (
          <dl className="space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-text-secondary">Production cost</dt>
              <dd className="font-medium text-text-primary">
                {formatMoney(productionCost, currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-text-secondary">Shipping cost (US ground)</dt>
              <dd className="font-medium text-text-primary">
                {formatMoney(shippingCost, currency)}
              </dd>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-1.5">
              <dt className="text-text-primary font-semibold">
                Total TK will invoice
              </dt>
              <dd className="text-text-primary font-semibold">
                {formatMoney(totalCost, currency)}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div className="border-t border-border px-4 py-3 sm:px-5">
        <p className="text-[11px] text-text-secondary">
          Customer paid{' '}
          <span className="font-medium text-text-primary">
            {formatMoney(customerPaidTotal, currency)}
          </span>{' '}
          to the merchant on Shopify. The wholesale cost above is billed to
          the merchant by Transfer Kingdom and is independent of that total.
        </p>
        {computedAt && (
          <p className="mt-1 text-[10px] text-text-tertiary">
            Snapshot taken {new Date(computedAt).toLocaleString()}
          </p>
        )}
        <p className="mt-2 text-[11px] text-text-secondary">
          Settled from your Transfer Kingdom{' '}
          <Link
            to="/billing?tab=wallet"
            className="font-medium text-accent hover:underline"
          >
            wallet
          </Link>{' '}
          at order ingest. Production is gated until the charge clears.
        </p>
      </div>

      {/* Settlement action — only when the charge needs the merchant to act. */}
      {needsAttention && (
        <div
          className="border-t border-border px-4 py-3 sm:px-5"
          style={{ backgroundColor: 'var(--color-banner-warning-bg)' }}
        >
          <p
            className="text-xs font-medium"
            style={{ color: 'var(--color-banner-warning-text)' }}
          >
            {status === WholesaleChargeStatus.ON_HOLD
              ? 'This order is on hold until its wholesale cost is settled.'
              : 'This order’s wholesale cost has not been collected yet.'}{' '}
            Production will not start until the charge clears.
          </p>
          <p className="mt-1 text-[11px] text-text-secondary">
            {guidance ??
              'Add funds to your wallet or set a default card, then retry the charge.'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Link
              to="/billing?tab=wallet"
              className="inline-flex items-center rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text-primary transition-colors hover:bg-surface-secondary"
            >
              Add funds
            </Link>
            <Link
              to="/billing?tab=payment-methods"
              className="inline-flex items-center rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-medium text-text-primary transition-colors hover:bg-surface-secondary"
            >
              Payment methods
            </Link>
            {onRetry && (
              <Button
                variant="primary"
                size="sm"
                onClick={onRetry}
                loading={retrying}
                disabled={retrying}
              >
                {retrying ? 'Retrying…' : 'Retry charge'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
